
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function AIClonePage() {
  const features = [
    {
      icon: 'ri-time-fill',
      title: '24/7 Availability',
      description: 'Your AI clone works around the clock, engaging with prospects even when you\'re sleeping or with other clients.'
    },
    {
      icon: 'ri-voice-recognition-fill',
      title: 'Voice & Text Interaction',
      description: 'Supports both voice calls and text conversations, providing natural and engaging interactions.'
    },
    {
      icon: 'ri-user-heart-fill',
      title: 'Personalized Responses',
      description: 'Trained on your communication style and expertise to provide authentic, personalized responses.'
    },
    {
      icon: 'ri-global-fill',
      title: 'Multi-language Support',
      description: 'Communicate with prospects in multiple languages to expand your market reach.'
    },
    {
      icon: 'ri-database-2-fill',
      title: 'CRM Integration',
      description: 'Seamlessly integrates with your existing CRM to track interactions and update lead information.'
    },
    {
      icon: 'ri-bar-chart-fill',
      title: 'Performance Analytics',
      description: 'Detailed analytics on conversations, lead quality, and conversion rates to optimize performance.'
    }
  ];

  const benefits = [
    'Increase lead response time from hours to seconds',
    'Handle multiple prospects simultaneously',
    'Never miss a lead due to availability',
    'Consistent messaging and brand representation',
    'Reduce workload while increasing conversions',
    'Scale your business without hiring more staff'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 64, 175, 0.8), rgba(59, 130, 246, 0.6)), url('https://public.readdy.ai/ai/img_res/301982018908a51d0c35a6c1b14f55d6.jpg')`
        }}
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">
              AI Clone Service: Your Digital Twin That Never Sleeps
            </h1>
            <p className="text-xl mb-8 leading-relaxed">
              Create an AI-powered version of yourself that engages prospects 24/7, qualifies leads, and provides personalized responses using your expertise and communication style.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                  <i className="ri-play-circle-fill mr-2"></i>
                  Try Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
                  <i className="ri-phone-fill mr-2"></i>
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Advanced AI Clone Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI clone technology creates a digital version of you that can handle complex real estate conversations with the same expertise and personality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Transform Your Lead Generation
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Your AI clone works tirelessly to engage every prospect, ensuring no lead goes unattended while maintaining your personal touch and expertise.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20real%20estate%20agent%20using%20AI%20technology%20on%20tablet%2C%20holographic%20interface%20showing%20client%20conversations%2C%20modern%20office%20setting%2C%20futuristic%20business%20technology%2C%20professional%20lighting&width=600&height=500&seq=17&orientation=landscape"
                alt="AI Clone in Action"
                className="w-full rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How Your AI Clone Works
            </h2>
            <p className="text-xl text-gray-600">
              Simple setup process that creates a powerful AI version of yourself in just a few steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Training Phase</h3>
              <p className="text-gray-600">
                We analyze your communication style, expertise, and preferences to create your unique AI personality.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Integration</h3>
              <p className="text-gray-600">
                Your AI clone integrates with your website, CRM, and communication channels for seamless operation.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Engagement</h3>
              <p className="text-gray-600">
                Your AI clone starts engaging prospects 24/7, qualifying leads and scheduling appointments automatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">AI Clone Service Pricing</h2>
                <p className="text-lg text-gray-600">
                  Choose the plan that fits your business needs and scale as you grow.
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="text-center p-6 border border-gray-200 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Starter</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-4">$299<span className="text-lg text-gray-500">/month</span></div>
                  <ul className="text-gray-600 space-y-2 text-sm">
                    <li>Up to 100 conversations/month</li>
                    <li>Basic CRM integration</li>
                    <li>Email support</li>
                  </ul>
                </div>
                
                <div className="text-center p-6 border-2 border-blue-600 rounded-xl relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm">
                    Most Popular
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Professional</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-4">$599<span className="text-lg text-gray-500">/month</span></div>
                  <ul className="text-gray-600 space-y-2 text-sm">
                    <li>Up to 500 conversations/month</li>
                    <li>Advanced CRM integration</li>
                    <li>Voice & text support</li>
                    <li>Priority support</li>
                  </ul>
                </div>
                
                <div className="text-center p-6 border border-gray-200 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Enterprise</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-4">$999<span className="text-lg text-gray-500">/month</span></div>
                  <ul className="text-gray-600 space-y-2 text-sm">
                    <li>Unlimited conversations</li>
                    <li>Custom integrations</li>
                    <li>Multi-language support</li>
                    <li>Dedicated support</li>
                  </ul>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <Link to="/contact">
                  <Button size="lg">
                    Get Started Today
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
